import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';

/**
 * Recuerda que en el formulario de alta se emitió un escrito y lo adjunta al trámite apenas
 * este existe.
 *
 * EL PROBLEMA QUE RESUELVE
 * Los botones de escrito (Amparo por mora, Amparo PNC) tienen dos modos: desde la ficha generan
 * a partir del trámite guardado y dejan el .docx en sus Archivos; desde el alta solo generan y
 * descargan, porque todavía no hay idTramite al que colgarlo. Esa segunda rama perdía el
 * documento: el operador lo emitía al dar de alta, entraba a la ficha y no estaba.
 *
 * Es el mismo patrón —y a propósito el mismo vocabulario— que `usePactosPendientes`, que ya
 * resolvía esto para el PACTO. La diferencia es que acá no hay que reenviar nada: el trámite
 * recién creado ya tiene los datos, así que alcanza con llamar al endpoint "por trámite", que
 * regenera el documento y lo adjunta. El operador ya se descargó su copia al emitirlo, así que
 * esta segunda llamada no vuelve a descargar nada.
 *
 * Uso:
 *
 *     const { registrarEscrito, adjuntarATramite } = useEscritoPendiente(generarEscritoAmparoTramite);
 *     ...
 *     <BotonGenerarEscritoAmparo datos={datosEscrito} onGenerado={registrarEscrito} />
 *     ...
 *     const creado = await crearTramiteAmparo(payload);
 *     await adjuntarATramite(creado.idTramite);
 *
 * @param generarPorTramite  servicio `(idTramite) => Promise` que genera el escrito desde el
 *                           trámite ya guardado y lo adjunta a sus Archivos.
 */
export default function useEscritoPendiente(generarPorTramite) {
    const [pendiente, setPendiente] = useState(false);

    const registrarEscrito = useCallback(() => setPendiente(true), []);

    const adjuntarATramite = useCallback(async (idTramite) => {
        if (!idTramite || !pendiente) {
            return;
        }
        try {
            await generarPorTramite(idTramite);
        } catch (error) {
            // El trámite YA se creó cuando esto corre: un error acá no puede tumbar el alta.
            // Se avisa y el escrito se vuelve a generar desde la ficha cuando se quiera.
            console.error('❌ Error adjuntando el escrito al trámite:', error);
            toast.warn('El trámite se creó, pero no se pudo adjuntar el escrito. Generalo de nuevo desde la ficha.');
        }
    }, [pendiente, generarPorTramite]);

    return { registrarEscrito, adjuntarATramite };
}
